import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { authAPI, dataAPI, uploadFileToS3 } from '../lib/api';
import { getRoleLabel, getErrorMessage } from '../lib/utils';
import { toast } from 'sonner';
import {
  User,
  Phone,
  MapPin,
  Building,
  Save,
  ArrowLeft,
  Camera,
  Loader2,
  X,
} from 'lucide-react';
import { Link } from 'react-router-dom';

export default function SettingsPage() {
  const { user, setUser } = useAuth();
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    company_name: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
  });
  const [profilePhoto, setProfilePhoto] = useState(null);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await dataAPI.getStates();
        setStates(response.data);
      } catch (error) {
        console.error('Error fetching states:', error);
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const response = await authAPI.getMe();
        const userData = response.data;
        setFormData({
          name: userData.name || '',
          phone: userData.phone || '',
          company_name: userData.company_name || '',
          address_street: userData.address_street || '',
          address_city: userData.address_city || '',
          address_state: userData.address_state || '',
          address_zip: userData.address_zip || '',
        });
        setProfilePhoto(userData.profile_photo || null);
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };
    loadUserData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleStateChange = (value) => {
    setFormData(prev => ({ ...prev, address_state: value === 'none' ? '' : value }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Selecciona un archivo de imagen');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe pesar menos de 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const { public_url } = await uploadFileToS3(file, 'users');

      await authAPI.updateProfile({ profile_photo: public_url });
      setProfilePhoto(public_url);

      const meRes = await authAPI.getMe();
      localStorage.setItem('user', JSON.stringify(meRes.data));
      if (setUser) setUser(meRes.data);

      toast.success('Foto de perfil actualizada');
    } catch (error) {
      toast.error('No se pudo subir la foto');
      console.error(error);
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = async () => {
    try {
      await authAPI.updateProfile({ profile_photo: '' });
      setProfilePhoto(null);
      const meRes = await authAPI.getMe();
      localStorage.setItem('user', JSON.stringify(meRes.data));
      if (setUser) setUser(meRes.data);
      toast.success('Foto de perfil eliminada');
    } catch (error) {
      toast.error('No se pudo eliminar la foto');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (value && value.trim()) {
          updateData[key] = value.trim();
        }
      });

      if (Object.keys(updateData).length === 0) {
        toast.error('Llena al menos un campo');
        setLoading(false);
        return;
      }

      await authAPI.updateProfile(updateData);
      toast.success('Perfil actualizado correctamente');

      const meResponse = await authAPI.getMe();
      localStorage.setItem('user', JSON.stringify(meResponse.data));
      if (setUser) setUser(meResponse.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo actualizar el perfil'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary" data-testid="settings-page">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Link to="/dashboard">
              <Button variant="ghost" className="text-muted-foreground" data-testid="back-btn">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver al panel
              </Button>
            </Link>
          </div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">Configuración del perfil</h1>
          <p className="text-muted-foreground mt-1">
            Información de tu cuenta
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        {/* Profile Photo */}
        <div className="bg-white border border-border rounded-md p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-accent p-2 rounded-sm">
              <Camera className="h-5 w-5 text-white" />
            </div>
            <h2 className="font-heading text-xl font-bold uppercase tracking-tight">Foto de perfil</h2>
          </div>

          <div className="flex items-center gap-6">
            <div className="relative shrink-0">
              {profilePhoto ? (
                <img
                  src={profilePhoto}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover border-2 border-border"
                  data-testid="profile-photo-preview"
                />
              ) : (
                <div
                  className="w-24 h-24 rounded-full bg-secondary border-2 border-dashed border-muted flex items-center justify-center"
                  data-testid="profile-photo-placeholder"
                >
                  <User className="h-10 w-10 text-muted-foreground" />
                </div>
              )}

              {uploadingPhoto && (
                <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center">
                  <Loader2 className="h-6 w-6 text-white animate-spin" />
                </div>
              )}
            </div>

            <div className="space-y-3">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoUpload}
                accept="image/*"
                className="hidden"
                data-testid="photo-file-input"
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  data-testid="upload-photo-btn"
                >
                  {uploadingPhoto ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Subiendo...</>
                  ) : (
                    <><Camera className="h-4 w-4 mr-2" />{profilePhoto ? 'Cambiar foto' : 'Subir foto'}</>
                  )}
                </Button>
                {profilePhoto && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleRemovePhoto}
                    className="text-red-600"
                    data-testid="remove-photo-btn"
                  >
                    <X className="h-4 w-4 mr-1" />Eliminar</Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">JPG, PNG o WebP. Máx. 5MB.</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Personal Information */}
          <div className="bg-white border border-border rounded-md p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-primary p-2 rounded-sm">
                <User className="h-5 w-5 text-white" />
              </div>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight">
                Información personal
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-semibold uppercase tracking-wide">Nombre completo</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Juan Pérez"
                  className="h-12"
                  data-testid="profile-name"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">Correo electrónico</Label>
                <Input
                  value={user?.email || ''}
                  disabled
                  className="h-12 bg-secondary"
                  data-testid="profile-email"
                />
                <p className="text-xs text-muted-foreground">El correo no se puede cambiar</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-semibold uppercase tracking-wide">Número de teléfono</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="h-12 pl-10"
                    data-testid="profile-phone"
                  />
                </div>
              </div>

              {(user?.role === 'dealer' || user?.role === 'seller') && (
                <div className="space-y-2">
                  <Label htmlFor="company_name" className="text-sm font-semibold uppercase tracking-wide">Nombre de la empresa</Label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="company_name"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      placeholder="Camiones del Pacífico S.A."
                      className="h-12 pl-10"
                      data-testid="profile-company"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-white border border-border rounded-md p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-accent p-2 rounded-sm">
                <MapPin className="h-5 w-5 text-white" />
              </div>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight">
                Dirección
              </h2>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="address_street" className="text-sm font-semibold uppercase tracking-wide">
                  Calle y número
                </Label>
                <Input
                  id="address_street"
                  name="address_street"
                  value={formData.address_street}
                  onChange={handleChange}
                  placeholder="Av. Principal 123, Col. Centro"
                  className="h-12"
                  data-testid="profile-street"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="address_city" className="text-sm font-semibold uppercase tracking-wide">Ciudad</Label>
                  <Input
                    id="address_city"
                    name="address_city"
                    value={formData.address_city}
                    onChange={handleChange}
                    placeholder="Ciudad de México"
                    className="h-12"
                    data-testid="profile-city"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold uppercase tracking-wide">
                    Estado/Provincia
                  </Label>
                  <Select
                    value={formData.address_state || 'none'}
                    onValueChange={handleStateChange}
                  >
                    <SelectTrigger className="h-12" data-testid="profile-state">
                      <SelectValue placeholder="Seleccione departamento" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Seleccione departamento</SelectItem>
                      {states.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address_zip" className="text-sm font-semibold uppercase tracking-wide">
                    Código postal
                  </Label>
                  <Input
                    id="address_zip"
                    name="address_zip"
                    value={formData.address_zip}
                    onChange={handleChange}
                    placeholder="06000"
                    className="h-12"
                    data-testid="profile-zip"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Account Info */}
          <div className="bg-white border border-border rounded-md p-6 mb-6">
            <h2 className="font-heading text-xl font-bold uppercase tracking-tight mb-4">
              Información de la cuenta
            </h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Tipo de cuenta:</span>
                <span className="ml-2 font-semibold">{getRoleLabel(user?.role)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Suscripción:</span>
                <span className="ml-2 capitalize font-semibold">{user?.subscription_plan === 'free' ? 'Gratis' : user?.subscription_plan === 'pro' ? 'Pro' : user?.subscription_plan === 'enterprise' ? 'Empresa' : (user?.subscription_plan || 'Gratis')}</span>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              className="btn-accent"
              disabled={loading}
              data-testid="save-profile-btn"
            >
              {loading ? (
                'Guardando...'
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />Guardar cambios</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
